import './dragAndDropIndicator.less';

const INSERT_BEFORE_CLASS = 'drag-drop-insert-before';
const INSERT_AFTER_CLASS = 'drag-drop-insert-after';
const VERTICAL_CLASS = 'drag-drop-vertical';
// Below this many pixels of movement, a mousedown+mouseup on a swatch is treated as a click
// (selecting/removing a color), not the start of a reorder drag.
const DRAG_START_THRESHOLD_PX = 4;

type DragAndDropOrientation = 'horizontal' | 'vertical';

function elementCenter(element: HTMLElement, orientation: DragAndDropOrientation): number {
	const rect = element.getBoundingClientRect();
	return orientation === 'vertical' ? rect.top + rect.height / 2 : rect.left + rect.width / 2;
}

export function createNode(html: string, parent?: Element): HTMLElement {
	const container = document.createElement('div');
	container.innerHTML = html;

	const newNode = container.children[0] as HTMLElement;

	if (parent) {
		parent.appendChild(newNode);
	}

	return newNode;
}

type AddDragAndDropOptions = {
	onDrop: (data: string) => void;
	overClassName: string;
	data: string;
	dataKey: string;
};

export function addDragAndDrop(element: HTMLElement, options: AddDragAndDropOptions) {
	let counter = 0;

	element.draggable = true;
	element.addEventListener('dragstart', (e) => {
		if (e.currentTarget instanceof HTMLDivElement) {
			e.currentTarget.style.opacity = '0.4';
			e.dataTransfer.setData(options.dataKey, options.data);
		}
	});

	element.addEventListener('dragend', (e) => {
		if (e.currentTarget instanceof HTMLDivElement) {
			e.currentTarget.style.opacity = '1';
		}
	});

	element.addEventListener('dragover', (e) => {
		for (const item of e.dataTransfer.items) {
			if (item.type == options.dataKey) {
				e.preventDefault();
			}
		}
	});

	element.addEventListener('dragenter', (e) => {
		counter++;
		if (e.currentTarget instanceof HTMLDivElement) {
			element.classList.add(options.overClassName);
		}
	});

	element.addEventListener('dragleave', (e) => {
		counter--;
		if (e.currentTarget instanceof HTMLDivElement) {
			if (counter <= 0) {
				element.classList.remove(options.overClassName);
			}
		}
	});

	element.addEventListener('dragend', () => {
		console.log('end');
	});

	element.addEventListener('drop', (e) => {
		if (e.currentTarget instanceof HTMLDivElement) {
			counter = 0;
			element.classList.remove(options.overClassName);
			const data = e.dataTransfer.getData(options.dataKey);
			if (data != null) {
				options.onDrop(data);
				e.preventDefault();
			}
		}
	});
}

type AddDragAndDropOnContainerOptions = {
	selector: string;
	// insertAfter reflects which half of targetChild the cursor was over at drop time (left/top →
	// false, right/bottom → true), so callers can place the dragged item before or after it accordingly.
	onDrop: (targetChild: HTMLElement, draggedData: string, insertAfter: boolean) => void;
	getData: (child: HTMLElement) => string;
	orientation?: DragAndDropOrientation;
};

export function addDragAndDropOnContainer(container: HTMLElement, options: AddDragAndDropOnContainerOptions) {
	const orientation = options.orientation ?? 'horizontal';

	let indicatorTarget: HTMLElement | null = null;

	const clearIndicator = () => {
		indicatorTarget?.classList.remove(INSERT_BEFORE_CLASS, INSERT_AFTER_CLASS, VERTICAL_CLASS);
		indicatorTarget = null;
	};

	const findMatch = (target: EventTarget | null): HTMLElement | null => {
		if (!(target instanceof HTMLElement)) return null;
		const match = target.closest<HTMLElement>(options.selector);
		return match && container.contains(match) ? match : null;
	};

	const resolveTarget = (target: EventTarget | null, clientPos: number): HTMLElement | null => {
		const direct = findMatch(target);
		if (direct) return direct;

		//it might be what a curser is exactly between elements and findMatch would not return anything
		//so we are still trying to pick one
		const candidates = Array.from(container.querySelectorAll<HTMLElement>(options.selector));
		if (candidates.length === 0) return null;

		return candidates.reduce((closest, el) => {
			const distance = Math.abs(elementCenter(el, orientation) - clientPos);
			return distance < Math.abs(elementCenter(closest, orientation) - clientPos) ? el : closest;
		});
	};

	let source: HTMLElement | null = null;
	let sourceData: string | null = null;
	let startX = 0;
	let startY = 0;
	let dragging = false;
	let previousBodyUserSelect = '';

	// A click that lands right after a real drag would otherwise also fire on whatever the swatch
	// got dropped onto (e.g. selecting that color) - swallow exactly that one click, capture-phase
	// so it never reaches the target's own handlers.
	const suppressNextClick = (e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		container.removeEventListener('click', suppressNextClick, true);
	};

	const endDrag = () => {
		document.removeEventListener('mousemove', onMouseMove);
		document.removeEventListener('mouseup', onMouseUp);
		window.removeEventListener('blur', onWindowBlur);

		if (source) source.style.opacity = '1';
		if (dragging) document.body.style.userSelect = previousBodyUserSelect;

		source = null;
		sourceData = null;
		dragging = false;
	};

	const onWindowBlur = () => {
		clearIndicator();
		endDrag();
	};

	const onMouseMove = (e: MouseEvent) => {
		if (!source) return;

		if (!dragging) {
			if (
				Math.abs(e.clientX - startX) < DRAG_START_THRESHOLD_PX &&
				Math.abs(e.clientY - startY) < DRAG_START_THRESHOLD_PX
			) {
				return;
			}
			dragging = true;
			source.style.opacity = '0.4';
			previousBodyUserSelect = document.body.style.userSelect;
			document.body.style.userSelect = 'none';
		}

		const child = resolveTarget(e.target, orientation === 'vertical' ? e.clientY : e.clientX);
		if (!child) {
			clearIndicator();
			return;
		}

		if (child !== indicatorTarget) clearIndicator();
		const rect = child.getBoundingClientRect();
		const insertAfter =
			orientation === 'vertical' ? e.clientY - rect.top > rect.height / 2 : e.clientX - rect.left > rect.width / 2;
		child.classList.toggle(INSERT_BEFORE_CLASS, !insertAfter);
		child.classList.toggle(INSERT_AFTER_CLASS, insertAfter);
		child.classList.toggle(VERTICAL_CLASS, orientation === 'vertical');
		indicatorTarget = child;
	};

	const onMouseUp = (e: MouseEvent) => {
		const wasDragging = dragging;
		const data = sourceData;

		if (wasDragging) {
			const child = resolveTarget(e.target, orientation === 'vertical' ? e.clientY : e.clientX);
			clearIndicator();

			if (child && data != null) {
				const rect = child.getBoundingClientRect();
				const insertAfter =
					orientation === 'vertical' ? e.clientY - rect.top > rect.height / 2 : e.clientX - rect.left > rect.width / 2;
				options.onDrop(child, data, insertAfter);
			}

			container.addEventListener('click', suppressNextClick, true);
		}

		endDrag();
	};

	const onMouseDown = (e: MouseEvent) => {
		if (e.button !== 0) return;

		const child = findMatch(e.target);
		if (!child) return;

		source = child;
		sourceData = options.getData(child);
		startX = e.clientX;
		startY = e.clientY;
		dragging = false;

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
		window.addEventListener('blur', onWindowBlur);
	};

	container.addEventListener('mousedown', onMouseDown);

	return () => {
		container.removeEventListener('mousedown', onMouseDown);
		clearIndicator();
		endDrag();
	};
}

export function htmlToText(html: string) {
	const div = document.createElement('div');
	div.innerHTML = html;
	return div.innerText;
}

export function emulateFileDownload(name: string, content: Blob) {
	const fileName = decodeURI(name);
	const url = window.URL.createObjectURL(content);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', fileName);
	document.body.appendChild(link);
	link.click();
	window.URL.revokeObjectURL(url);
	link.remove();
}
